import React, { useEffect, useState } from "react";
import "./order-filter.scss";
import {
  ORDER_STATUS_OPTIONS,
  orderFilterOptions,
  PAYMENT_STATUS_OPTIONS,
  PLAN_NAME_OPTIONS,
  PLANTYPE_OPTIONS,
} from "../../order-management-helper";
import { camelCase } from "lodash";
import dayjs from "dayjs";
import CloseIcon from "@mui/icons-material/Close";
import { Form, Formik } from "formik";
import { Checkbox, ListItemText, MenuItem } from "@mui/material";
import OnboardedDateFilter from "../../../../tenant/tenant-list/tenant-filter-options/onboardeddate-filter";
import CompanyFilter from "../../../../leads/lead-logs/leads-log-filters/leads-log-filter-options/company-filter/company-filter";
import SubscriptionPlanFilter from "../../../../tenant/tenant-list/tenant-filter-options/subscription-plan";

interface CompanyOption {
  id: number;
  company_name: string;
}
const OrderFilters: React.FC<{
  open: boolean;
  onClose: () => void;
  onApplyFilters: (filters: Record<string, any>, shouldClose?: boolean) => void;
  filters: Record<string, any>;
  companyNames?: CompanyOption[];
}> = ({ open, onClose, onApplyFilters, filters, companyNames }) => {
  const [filterOptions, setFilterOptions] = useState(() =>
    orderFilterOptions.map((opt) => ({ ...opt }))
  );
  const isAnyFilterSelected = filterOptions.some((opt) => opt.value);

  const handleCheckboxChange = (
    label: string,
    values: Record<string, any>,
    setFieldValue: (field: string, value: any) => void
  ) => {
    const updated = filterOptions.map((item) => {
      if (item.label === label) {
        const isChecked = !item.value;

        if (!isChecked) {
          if (label === "Order Date") {
            setFieldValue("orderFromDate", null);
            setFieldValue("orderToDate", null);
          } else if (label === "Plan Frequency") {
            setFieldValue("planType", []);
          } else {
            const key = camelCase(label);
            setFieldValue(key, Array.isArray(values[key]) ? [] : "");
          }
        }

        return { ...item, value: isChecked };
      }
      return item;
    });

    setFilterOptions(updated);
  };

  const getInitialValues = () => {
    const values: Record<string, any> = {};
    filterOptions.forEach((option) => {
      if (option.label === "Order Date") {
        values["orderFromDate"] = null;
        values["orderToDate"] = null;
      } else if (option.label === "Plan Frequency") {
        values["planType"] = [];
      } else {
        const key = camelCase(option.label);
        values[key] = [];
      }
    });
    return values;
  };
  const [formValues, setFormValues] = useState(getInitialValues());

  const handleApply = (values: Record<string, any>) => {
    const selected = filterOptions
      .filter((opt) => opt.value)
      .reduce(
        (acc, curr) => {
          if (curr.label === "Order Date") {
            acc["orderFromDate"] = values["orderFromDate"];
            acc["orderToDate"] = values["orderToDate"];
          } else if (curr.label === "Plan Frequency") {
            acc["planType"] = values["planType"];
          } else {
            const key = camelCase(curr.label);
            acc[key] = values[key];
          }
          return acc;
        },
        {} as Record<string, any>
      );
    setFormValues(values);
    onApplyFilters(selected);
  };

  const validate = (values: Record<string, any>) => {
    const errors: Record<string, string> = {};
    const minDate = dayjs("1900-01-01");

    filterOptions.forEach((option) => {
      if (option.value) {
        if (option.label === "Company Name") {
          if (!values["companyName"] || values["companyName"].length === 0) {
            errors["companyName"] = "Company name cannot be empty";
          }
        } else if (option.label === "Order Date") {
          const startDate = values["orderFromDate"]
            ? dayjs(values["orderFromDate"])
            : null;
          const endDate = values["orderToDate"]
            ? dayjs(values["orderToDate"])
            : null;

          if (!startDate) {
            errors["orderFromDate"] = "Start date is required";
          } else if (startDate.isBefore(minDate)) {
            errors["orderFromDate"] = "Invalid date";
          }

          if (!endDate) {
            errors["orderToDate"] = "End date is required";
          } else if (endDate.isBefore(minDate)) {
            errors["orderToDate"] = "Invalid date";
          }
        } else if (option.label === "Plan Frequency") {
          if (!values["planType"] || values["planType"].length === 0) {
            errors["planType"] = "Plan frequency cannot be empty";
          }
        } else {
          const key = camelCase(option.label);
          if (
            !values[key] ||
            (Array.isArray(values[key]) && values[key].length === 0)
          ) {
            errors[key] = `${option.label} cannot be empty`;
          }
        }
      }
    });

    return errors;
  };

  const renderFilterField = (
    label: string,
    values: Record<string, any>,
    errors?: Record<string, any>,
    touched?: Record<string, any>,
    setFieldValue?: (field: string, value: any) => void,
    handleBlur?: (e: any) => void
  ) => {
    switch (label) {
      case "Order Date":
        return (
          <OnboardedDateFilter
            startDate={values.orderFromDate}
            endDate={values.orderToDate}
            onDateRangeChange={(start, end) => {
              if (start) setFieldValue?.("orderFromDate", start);
              if (end) setFieldValue?.("orderToDate", end);
            }}
            startDateError={
              touched?.orderFromDate && errors?.orderFromDate
                ? String(errors.orderFromDate)
                : undefined
            }
            endDateError={
              touched?.orderToDate && errors?.orderToDate
                ? String(errors.orderToDate)
                : undefined
            }
          />
        );
      case "Company Name":
        return (
          <div className="company-filter-wrapper">
            <CompanyFilter
              options={companyNames ? companyNames : []}
              fieldName="companyName"
              value={values.companyName}
              onChange={(selected: (number | string)[] | null) =>
                setFieldValue?.("companyName", selected || [])
              }
              handleBlur={() =>
                handleBlur?.({ target: { name: "companyName" } })
              }
              isShowError={touched?.companyName && !!errors?.companyName}
              isOrder={true}
            />
          </div>
        );
      case "Payment Status":
        return (
          <SubscriptionPlanFilter
            value={
              Array.isArray(values["paymentStatus"])
                ? values["paymentStatus"]
                : []
            }
            plans={PAYMENT_STATUS_OPTIONS}
            label="Select Status"
            error={errors?.paymentStatus}
            onChange={(val: string[]) => setFieldValue?.("paymentStatus", val)}
            isMultiple={true}
          />
        );
      case "Order Status":
        return (
          <SubscriptionPlanFilter
            value={
              Array.isArray(values["orderStatus"]) ? values["orderStatus"] : []
            }
            plans={ORDER_STATUS_OPTIONS}
            label="Select Status"
            error={errors?.orderStatus}
            onChange={(val: string[]) => setFieldValue?.("orderStatus", val)}
            isMultiple={true}
          />
        );
      case "Plan Name":
        return (
          <SubscriptionPlanFilter
            value={Array.isArray(values["planName"]) ? values["planName"] : []}
            plans={PLAN_NAME_OPTIONS}
            error={errors?.planName}
            onChange={(val: string[]) => setFieldValue?.("planName", val)}
            isMultiple={true}
          />
        );
      case "Plan Frequency":
        return (
          <SubscriptionPlanFilter
            value={Array.isArray(values["planType"]) ? values["planType"] : []}
            plans={PLANTYPE_OPTIONS}
            label="Select Status"
            error={errors?.planType}
            onChange={(val: string[]) => setFieldValue?.("planType", val)}
            isMultiple={true}
          />
        );
      default:
        return null;
    }
  };

  useEffect(() => {
    const updatedOptions = orderFilterOptions.map((opt) => {
      const key =
        opt.label === "Order Date"
          ? "orderFromDate" in filters || "orderToDate" in filters
          : opt.label === "Plan Frequency"
            ? "planType" in filters
            : camelCase(opt.label) in filters;

      return { ...opt, value: !!key };
    });

    setFilterOptions(updatedOptions);

    setFormValues((prev) => ({
      ...getInitialValues(),
      ...filters,
    }));
  }, [filters]);

  return (
    <div className={`order-filter-drawer ${open ? "open" : ""}`}>
      <div className="filter-header">
        <div className="drawer-header">
          <h4>Filters</h4>
          <CloseIcon
            fontSize="small"
            onClick={onClose}
            style={{ cursor: "pointer" }}
          />
        </div>
        <h4>Filter By Fields</h4>
      </div>
      <Formik
        initialValues={formValues}
        enableReinitialize
        validate={validate}
        onSubmit={handleApply}
      >
        {({
          values,
          errors,
          touched,
          setFieldValue,
          handleSubmit,
          resetForm,
          handleBlur,
        }) => {
          return (
            <Form className="drawer-content" onSubmit={handleSubmit}>
              {filterOptions.map((option) => (
                <div key={option.label} className="filter-option-row">
                  <MenuItem
                    key={option.label}
                    value={option.label}
                    className="filter-menu-item"
                    onClick={() =>
                      handleCheckboxChange(option.label, values, setFieldValue)
                    }
                  >
                    <Checkbox
                      checked={option.value}
                    />
                    <ListItemText primary={option.label} />
                  </MenuItem>

                  {option.value &&
                    renderFilterField(
                      option.label,
                      values,
                      errors,
                      touched,
                      setFieldValue,
                      handleBlur
                    )}
                </div>
              ))}

              {isAnyFilterSelected && (
                <div className="lead-filter-buttons">
                  <button
                    type="button"
                    className="cancel-button"
                    onClick={() => {
                      resetForm();
                      setFormValues(getInitialValues());
                      setFilterOptions(
                        orderFilterOptions.map((opt) => ({ ...opt }))
                      );
                      onApplyFilters({}, false);
                    }}
                  >
                    Reset
                  </button>
                  <button type="submit" className="next-button-wrapper">
                    Apply
                  </button>
                </div>
              )}
            </Form>
          );
        }}
      </Formik>
    </div>
  );
};
export default OrderFilters;
